/**
 * Shared `POST /v1/feedback` path for every web feedback surface.
 *
 * Two consumers today: the in-app `FeedbackModal` (surface `webapp`) and
 * the post-uninstall churn page (surface `uninstall`). They differ in
 * three ways and nothing else, so the transport lives here rather than
 * being copied:
 *
 *   - the `X-Reflecto-Surface` value,
 *   - whether a device id is attached (see `deviceId` below),
 *   - whether the caller distinguishes a 429 from a generic failure —
 *     `FeedbackSubmitError` carries the status so it can.
 *
 * SECURITY: the message and email are user content. They go in the
 * request body and nowhere else — never a log line, never an error
 * message. `FeedbackSubmitError` deliberately carries only the status.
 */

import type { FeedbackPayload } from "@reflecto/client-ui";
import { API_BASE } from "../api/config";

/**
 * A non-2xx response from `POST /v1/feedback`. Carries the HTTP status
 * (and nothing from the request) so a caller can branch on 429 —
 * the only status the UI treats differently — without re-reading the
 * response.
 */
export class FeedbackSubmitError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`POST /feedback failed (${status})`);
    this.name = "FeedbackSubmitError";
    this.status = status;
  }

  /** The server's per-IP / per-surface / per-device hourly quota was hit. */
  get rateLimited(): boolean {
    return this.status === 429;
  }
}

export interface SubmitFeedbackOptions {
  /**
   * `X-Reflecto-Surface`. Must be a value the server's
   * `FEEDBACK_SURFACES` allowlist knows, or the request is a 400.
   */
  surface: "webapp" | "uninstall";
  /**
   * Optional correlation id, sent as `X-Reflecto-Device-Id`.
   *
   * Opt-in rather than read from storage here on purpose: the uninstall
   * page's privacy note promises the form "is not linked to any device",
   * so that surface must never attach one — a default-on read would make
   * shipped copy false the moment a paired visitor lands there.
   */
  deviceId?: string;
}

/**
 * POST one feedback submission. Resolves on 2xx; throws
 * `FeedbackSubmitError` otherwise (and whatever `fetch` throws on a
 * network failure).
 */
export async function submitFeedback(
  payload: FeedbackPayload,
  { surface, deviceId }: SubmitFeedbackOptions
): Promise<void> {
  const res = await fetch(`${API_BASE}/feedback`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Reflecto-Surface": surface,
      "X-Reflecto-App-Version": __APP_VERSION__,
      ...(deviceId ? { "X-Reflecto-Device-Id": deviceId } : {}),
    },
    body: JSON.stringify({
      category: payload.category,
      message: payload.message,
      ...(payload.email ? { email: payload.email } : {}),
      // Honeypot — omit when empty (the common real-user case) rather than
      // send "". The server treats an absent website as a non-bot.
      ...(payload.website ? { website: payload.website } : {}),
    }),
  });
  if (!res.ok) {
    // Drain the body so the connection is released, matching api.ts.
    await res.json().catch(() => ({}));
    throw new FeedbackSubmitError(res.status);
  }
}
